import "../UserManagement.css";
import { useState, useMemo, useEffect } from "react";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../ui/table";
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Users,
    Shield,
    Plus,
    Edit,
    Trash2,
    MoreHorizontal,
    User,
    Crown,
    Settings,
} from "lucide-react";
import CardPagination from "../CardPagination";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import { Button } from "../../ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { useToast } from "../../../hooks/use-toast";
import { RoleUser, UserInRole, UserInRoles, UserRole } from "./interface";
import { myLocalStorage } from "../../StorageHelper";
import EmptyStateCard from "../EmptyStateCard";
import useUserStore from "../../../services/userStore";
import { createCustomRole, deleteRole, getAllUserRoles, getUsers, updateCustomRole } from "./Api/userrolesapi";
import { formatDate } from "../formatTimeAgo";

export default function UserRolesDetails() {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<UserRole | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState<UserRole | null>(null);
    const [editRoleName, setEditRoleName] = useState("");
    const [editRoleDescription, setEditRoleDescription] = useState("");
    const [newRoleName, setNewRoleName] = useState("");
    const [newRoleDescription, setNewRoleDescription] = useState("");
    const [usersDialogOpen, setUsersDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
    const [usersSearchTerm, setUsersSearchTerm] = useState("");
    const [usersCurrentPage, setUsersCurrentPage] = useState(1);
    const [usersPerPage] = useState(5);
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    useUserStore(
        (state) => state.user,
    );
    const [roles, setRoles] = useState<UserRole[]>([]);
    const [userInRoles, setUserInRoles] = useState<UserInRoles | null>(null);
    const [filteredUsers, setFilteredUsers] = useState<UserInRole[]>([])

    const getRoleDescription = (role: UserRole) => {
        if (!role.customRole) return "-";
        return role.description?.trim() || "-";
    };

    const filteredRoles = useMemo(() => {
        if (searchTerm.length > 0)
            return roles.filter(role =>
                role.roleName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        else
            return roles
    }, [searchTerm, roles]);

    // Pagination for roles
    const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentRoles = filteredRoles.slice(startIndex, endIndex);

    const filteredUsersInRole = useMemo(() => {
        if (usersSearchTerm.length > 0) {
            const term = usersSearchTerm.toLowerCase().trim();
            return filteredUsers.filter(user => {
                const firstName = user.firstName?.toLowerCase() ?? "";
                const lastName = user.lastName?.toLowerCase() ?? "";
                const email = user.email?.toLowerCase() ?? "";
                const fullName = `${firstName} ${lastName}`.trim();
                return (
                    firstName.includes(term) ||
                    lastName.includes(term) ||
                    fullName.includes(term) ||
                    email.includes(term)
                );
            });
        }
        return filteredUsers;
    }, [usersSearchTerm, filteredUsers]);

    const usersTotalPages = Math.ceil(filteredUsersInRole.length / usersPerPage);
    const usersStartIndex = (usersCurrentPage - 1) * usersPerPage;
    const usersEndIndex = usersStartIndex + usersPerPage;
    const currentUsersInRole = filteredUsersInRole.slice(usersStartIndex, usersEndIndex);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleUsersPageChange = (page: number) => {
        setUsersCurrentPage(page);
    };

    const handleDeleteClick = (role: UserRole) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (roleToDelete) {
            await deleteRole(tenantName, roleToDelete.id);
            toast({
                title: "Role Deleted",
                description: `${roleToDelete.roleName} has been deleted successfully.`,
            });
            setDeleteDialogOpen(false);
            setRoleToDelete(null);
            loadUsers();
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
    };

    const handleEditClick = (role: UserRole) => {
        setRoleToEdit(role);
        setEditRoleName(role.roleName);
        setEditRoleDescription(role.description || "");
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (roleToEdit && editRoleName.trim()) {
            await updateCustomRole(
                tenantName,
                editRoleName,
                roleToEdit.id,
                editRoleDescription,
            );

            toast({
                title: "Role Updated",
                description: `${roleToEdit.roleName} has been updated successfully.`,
            });
            setEditDialogOpen(false);
            setRoleToEdit(null);
            setEditRoleName("");
            setEditRoleDescription("");
            loadUsers();
        }
    };

    const handleEditCancel = () => {
        setEditDialogOpen(false);
        setRoleToEdit(null);
        setEditRoleName("");
        setEditRoleDescription("");
    };

    const handleAddRole = () => {
        setAddDialogOpen(true);
    };

    const handleAddSave = async () => {
        if (newRoleName.trim()) {
            await createCustomRole(tenantName, newRoleName, newRoleDescription);
            toast({
                title: "Role Created",
                description: `${newRoleName} has been created successfully.`,
            });
            setAddDialogOpen(false);
            setNewRoleName("");
            setNewRoleDescription("");
            loadUsers();
        }
    };

    const handleAddCancel = () => {
        setAddDialogOpen(false);
        setNewRoleName("");
        setNewRoleDescription("");
    };

    const handleViewUsers = (role: UserRole) => {
        if (!userInRoles) return;

        const roleUsers = userInRoles.roles.users[role.roleName] || [];


        const formattedUsers = roleUsers.map((user) => ({
            id: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            createdAt: formatDate(new Date(user.createdAt).toISOString()),
        }));

        setFilteredUsers(formattedUsers); // this triggers useMemo for filteredUsersInRole
        setSelectedRole(role);
        setUsersDialogOpen(true);
        setUsersSearchTerm("");
        setUsersCurrentPage(1);
    };


    const loadUsers = async () => {
        const userInRoles: UserInRoles = await getUsers(tenantName);
        const roles: UserRole[] = await getAllUserRoles(tenantName);

        const allRoleUsers: RoleUser[] = Object.values(userInRoles.roles.users)
            .flatMap(roleUsers => roleUsers);

        const roleCounts: Record<string, number> = allRoleUsers.reduce((acc, user) => {
            acc[user.roleName] = (acc[user.roleName] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        const rolesWithCount: UserRole[] = roles.filter(role => role.roleName !== "super-user").map(role => ({
            ...role,
            userCount: roleCounts[role.roleName] || 0,
        }));

        setRoles(rolesWithCount);
        setUserInRoles(userInRoles);
    };



    useEffect(() => {
        if (!tenantName) return;
        loadUsers();
    }, [tenantName])


    return (
        <div className="um-page">
            <div className="um-stat-grid">
                <div className="um-card um-stat">
                    <span className="um-stat-icon" aria-hidden="true"><Settings /></span>
                    <div className="min-w-0">
                        <span className="um-stat-value">{roles.length}</span>
                        <span className="um-stat-label">Total Roles</span>
                        <span className="um-stat-sub">Built-in and custom</span>
                    </div>
                </div>
                <div className="um-card um-stat" data-tone="amber">
                    <span className="um-stat-icon" aria-hidden="true"><Crown /></span>
                    <div className="min-w-0">
                        <span className="um-stat-value">{roles.filter(r => !r.customRole).length}</span>
                        <span className="um-stat-label">Built-in Roles</span>
                        <span className="um-stat-sub">System roles</span>
                    </div>
                </div>
                <div className="um-card um-stat" data-tone="violet">
                    <span className="um-stat-icon" aria-hidden="true"><Settings /></span>
                    <div className="min-w-0">
                        <span className="um-stat-value">{roles.filter(r => r.customRole).length}</span>
                        <span className="um-stat-label">Custom Roles</span>
                        <span className="um-stat-sub">Admin created</span>
                    </div>
                </div>
                <div className="um-card um-stat" data-tone="success">
                    <span className="um-stat-icon" aria-hidden="true"><Users /></span>
                    <div className="min-w-0">
                        <span className="um-stat-value">{roles.reduce((sum, role) => sum + role.userCount, 0)}</span>
                        <span className="um-stat-label">Total Users</span>
                        <span className="um-stat-sub">Across all roles</span>
                    </div>
                </div>
            </div>

            <div className="um-card um-toolbar">
                <div className="um-title">
                    <span className="um-title-icon" aria-hidden="true">
                        <Shield />
                    </span>
                    <h1 className="um-title-text" data-testid="text-platform-logs-title">
                        User Roles
                        <span className="um-title-count">{filteredRoles.length}</span>
                    </h1>
                </div>
                <div className="um-toolbar-actions">
                    <div className="um-search">
                        <Search aria-hidden="true" />
                        <Input
                            placeholder="Search roles..."
                            aria-label="Search roles"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            data-testid="input-search-roles"
                        />
                    </div>
                    <EndpointActionButton onClick={handleAddRole} data-testid="button-add-role">
                        <Plus className="w-4 h-4" />
                        Add Custom Role
                    </EndpointActionButton>
                </div>
            </div>

            <div className="um-card um-table-card">
                <div className="um-table-scroll">
                    <Table className="um-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Role Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead className="w-[280px]">Description</TableHead>
                                <TableHead>Users</TableHead>
                                <TableHead>Created By</TableHead>
                                <TableHead>Created Date</TableHead>
                                <TableHead className="w-[100px] text-center">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {currentRoles.length > 0 ? (
                                currentRoles.map((role) => (
                                    <TableRow key={role.id} data-testid={`row-role-${role.id}`}>
                                        <TableCell>
                                            <div className="um-role-name">
                                                <span aria-hidden="true"><Shield /></span>
                                                {role.roleName}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {!role.customRole ? (
                                                <span className="um-chip" data-tone="primary">
                                                    <Crown aria-hidden="true" />
                                                    Built-in
                                                </span>
                                            ) : (
                                                <span className="um-chip">
                                                    <Settings aria-hidden="true" />
                                                    Custom
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="um-description">{getRoleDescription(role)}</div>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                className="um-link-button no-default-hover-elevate"
                                                onClick={() => handleViewUsers(role)}
                                                data-testid={`button-view-users-${role.id}`}
                                            >
                                                <Users />
                                                {role.userCount} users
                                            </Button>
                                        </TableCell>
                                        <TableCell className="um-cell-muted">
                                            {!role.customRole ? "System" : role.createdBy}
                                        </TableCell>
                                        <TableCell className="um-cell-muted">
                                            {!role.customRole ? "—" : (role.createdAt ? formatDate(role.createdAt) : "—")}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {role.customRole ? (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            className="um-row-menu no-default-hover-elevate"
                                                            aria-label={`Actions for ${role.roleName}`}
                                                            data-testid={`button-actions-${role.id}`}
                                                        >
                                                            <MoreHorizontal />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="um-menu">
                                                        <DropdownMenuItem
                                                            onClick={() => handleEditClick(role)}
                                                            className="um-menu-item"
                                                            data-testid={`button-edit-${role.id}`}
                                                        >
                                                            <Edit />
                                                            Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={() => handleDeleteClick(role)}
                                                            className="um-menu-item"
                                                            data-tone="danger"
                                                            data-testid={`button-delete-${role.id}`}
                                                        >
                                                            <Trash2 />
                                                            Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            ) : (
                                                <span className="um-dash">—</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow className="um-state-row">
                                    <TableCell colSpan={7}>
                                        <div className="um-state">
                                            <span className="um-state-icon" aria-hidden="true">
                                                <Shield />
                                            </span>
                                            <p className="um-state-title">
                                                {searchTerm ? "No roles match your search" : "No roles found"}
                                            </p>
                                            <p className="um-state-text">
                                                {searchTerm
                                                    ? "Try adjusting your search terms."
                                                    : "Create a custom role to manage user permissions."}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                {filteredRoles.length > 0 && (
                    <div className="um-pagination">
                        <span className="um-pagination-summary">
                            Showing {startIndex + 1}–{Math.min(endIndex, filteredRoles.length)} of {filteredRoles.length} roles
                        </span>
                        <CardPagination
                            label="Role pages"
                            currentPage={currentPage}
                            totalPages={Math.max(1, totalPages)}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="um-dialog">
                    <AlertDialogHeader className="um-dialog-header">
                        <span className="um-dialog-icon" aria-hidden="true">
                            <Trash2 />
                        </span>
                        <div className="min-w-0">
                            <AlertDialogTitle className="um-dialog-title">Delete Role</AlertDialogTitle>
                            <AlertDialogDescription className="um-dialog-description">
                                Are you sure you want to delete the role "{roleToDelete?.roleName}"? This action cannot be undone.
                                {(roleToDelete?.userCount ?? 0) > 0 && (
                                    <span className="um-dialog-warning">
                                        Warning: This role is assigned to {roleToDelete?.userCount ?? 0} user(s). Deleting it will remove this role from all assigned users.
                                    </span>
                                )}
                            </AlertDialogDescription>
                        </div>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="um-dialog-footer">
                        <AlertDialogCancel onClick={handleDeleteCancel} className="um-dialog-button" data-testid="button-cancel-delete">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="um-dialog-button bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete"
                        >
                            Delete Role
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Edit Role Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5" />
                            Edit Role
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="editRoleName">Role Name</Label>
                            <Input
                                id="editRoleName"
                                value={editRoleName}
                                onChange={(e) => setEditRoleName(e.target.value)}
                                placeholder="Enter role name..."
                                data-testid="input-edit-role-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="editRoleDescription">Description</Label>
                            <Input
                                id="editRoleDescription"
                                value={editRoleDescription}
                                onChange={(e) => setEditRoleDescription(e.target.value)}
                                placeholder="Enter role description..."
                                data-testid="input-edit-role-description"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleEditCancel}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleEditSave}
                            disabled={!editRoleName.trim()}
                            data-testid="button-save-edit"
                        >
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Role Dialog */}
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5" />
                            Add Custom Role
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="newRoleName">Role Name</Label>
                            <Input
                                id="newRoleName"
                                value={newRoleName}
                                onChange={(e) => setNewRoleName(e.target.value)}
                                placeholder="Enter role name..."
                                data-testid="input-new-role-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="newRoleDescription">Description</Label>
                            <Input
                                id="newRoleDescription"
                                value={newRoleDescription}
                                onChange={(e) => setNewRoleDescription(e.target.value)}
                                placeholder="Enter role description..."
                                data-testid="input-new-role-description"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleAddCancel}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddSave}
                            disabled={!newRoleName.trim()}
                            data-testid="button-save-new"
                        >
                            Create Role
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Users in Role Dialog */}
            <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Users with Role: {selectedRole?.roleName}
                            <Badge
                                variant={!selectedRole?.customRole ? "default" : "secondary"}
                                className={!selectedRole?.customRole ? "bg-blue-500" : ""}
                            >
                                {!selectedRole?.customRole ? "Built-in" : "Custom"}
                            </Badge>
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                        {/* Search */}
                        <div className="relative flex-shrink-0">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                placeholder="Search users..."
                                value={usersSearchTerm}
                                onChange={(e) => {
                                    setUsersSearchTerm(e.target.value);
                                    setUsersCurrentPage(1);
                                }}
                                className="pl-10"
                                data-testid="input-search-users-in-role"
                            />
                        </div>

                        {/* Users Table */}
                        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
                            <Table>
                                <TableHeader className="sticky top-0 z-10 bg-background">
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Created At</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {currentUsersInRole.length > 0 ? (
                                        currentUsersInRole.map((user) => (
                                            <TableRow key={user.id} data-testid={`row-user-${user.id}`}>
                                                <TableCell className="font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-primary" />
                                                        {user.firstName} {user.lastName}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {user.email}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="default">
                                                        {user.createdAt}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} className="p-6">
                                                <EmptyStateCard
                                                    embedded
                                                    compact
                                                    icon={User}
                                                    title="No users in this role"
                                                    description="No users are currently assigned to this role."
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                    <DialogFooter className="flex-shrink-0 border-t pt-4">
                        <div className="flex w-full items-center justify-between gap-4">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                                {filteredUsersInRole.length > 0 && (
                                    <span className="text-sm text-muted-foreground">
                                        Showing {usersStartIndex + 1} to {Math.min(usersEndIndex, filteredUsersInRole.length)} of {filteredUsersInRole.length} users
                                    </span>
                                )}
                                {usersTotalPages > 1 && (
                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUsersPageChange(usersCurrentPage - 1)}
                                            disabled={usersCurrentPage === 1}
                                            data-testid="button-users-prev-page"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            Previous
                                        </Button>
                                        <span className="text-sm">
                                            Page {usersCurrentPage} of {usersTotalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUsersPageChange(usersCurrentPage + 1)}
                                            disabled={usersCurrentPage === usersTotalPages}
                                            data-testid="button-users-next-page"
                                        >
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <Button variant="outline" onClick={() => setUsersDialogOpen(false)}>
                                Close
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}